import { X, Users } from "lucide-react";
import { useState, useRef, useCallback } from "react";
import { getProgression, type GradeGroup } from "../../lib/quiz-module";
import { COLOR_PALETTES, HERO_EMOJIS } from "../../lib/child-presets";
import { PERSONA_DISPLAY_PROFILES as PERSONA_PROFILES } from "../../lib/persona-profiles";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import type { ChildGender } from "../../App";
import "./settings-sidebar.scss";

const HERO_EMOJIS_INITIAL = 12; // rows shown before "הצג עוד"

type SettingsSidebarProps = {
  activity?: "reading" | "vocabulary";
  saving?: boolean;
  saveError?: string;
  onRetrySave?: () => void;
  childName: string;
  onNameChange?: (name: string) => void;
  childGender: ChildGender;
  onGenderChange?: (gender: ChildGender) => void;
  gradeGroup?: GradeGroup;
  step: number;
  onStepChange?: (step: number) => void;
  readOnly?: boolean;
  colorPalette?: string;
  emoji?: string;
  onProfileChange?: (updates: { colorPalette?: string; emoji?: string }) => void;
  personaProfileId?: string;
  onPersonaProfileChange?: (id: string) => void;
  onClose: () => void;
  onSwitchChild?: () => void;
};

export default function SettingsSidebar({
  activity = "vocabulary",
  saving = false,
  saveError,
  onRetrySave,
  childName,
  onNameChange,
  childGender,
  onGenderChange,
  gradeGroup = "3-5",
  step,
  onStepChange,
  readOnly,
  colorPalette,
  emoji,
  onProfileChange,
  personaProfileId = "default",
  onPersonaProfileChange,
  onClose,
  onSwitchChild,
}: SettingsSidebarProps) {
  const progression = getProgression(gradeGroup);
  const [emojiExpanded, setEmojiExpanded] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const stableOnClose = useCallback(() => onClose(), [onClose]);
  useFocusTrap(sidebarRef, true, stableOnClose);

  const hasExtraEmojis = HERO_EMOJIS.length > HERO_EMOJIS_INITIAL;

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />
      <div className="sidebar" dir="rtl" ref={sidebarRef} role="dialog" aria-label="הגדרות">
        <div className="sidebar-header">
          <span className="sidebar-title">הגדרות</span>
          <button className="sidebar-close" onClick={onClose} aria-label="סגור הגדרות">
            <X className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
          </button>
        </div>

        {onSwitchChild && <button type="button" className="sidebar-switch-child" onClick={onSwitchChild}><Users className="brand-icon" aria-hidden="true" />החלפת ילד</button>}

        {/* ── Section: הפרופיל שלי ── */}
        <fieldset className="sidebar-section sidebar-profile" disabled={saving}>
          <legend className="sidebar-section-title">הפרופיל שלי</legend>

          <div className="sidebar-field">
            <label className="sidebar-label">שם</label>
            {readOnly ? (
              <span className="sidebar-value">{childName || "—"}</span>
            ) : (
              <input
                className="sidebar-input"
                type="text"
                value={childName}
                onChange={(e) => onNameChange?.(e.target.value)}
                placeholder="הכניסו שם..."
              />
            )}
          </div>

          <div className="sidebar-field">
            <label className="sidebar-label">בן או בת?</label>
            {readOnly ? (
              <span className="sidebar-value">
                {childGender === "boy" ? "בן" : childGender === "girl" ? "בת" : "—"}
              </span>
            ) : (
              <div className="gender-toggle" role="group" aria-label="בן או בת">
                <button
                  className={`gender-chip ${childGender === "boy" ? "active" : ""}`}
                  aria-pressed={childGender === "boy"}
                  onClick={() => onGenderChange?.(childGender === "boy" ? "" : "boy")}
                >
                  בן
                </button>
                <button
                  className={`gender-chip ${childGender === "girl" ? "active" : ""}`}
                  aria-pressed={childGender === "girl"}
                  onClick={() => onGenderChange?.(childGender === "girl" ? "" : "girl")}
                >
                  בת
                </button>
              </div>
            )}
          </div>

          {onProfileChange && (
            <>
              <div className="sidebar-field">
                <label className="sidebar-label">הדמות שלי</label>
                <div
                  className={`emoji-grid ${hasExtraEmojis && !emojiExpanded ? "collapsed" : ""}`}
                >
                  {HERO_EMOJIS.map((e) => (
                    <button
                      key={e}
                      className={`emoji-chip ${emoji === e ? "active" : ""}`}
                      aria-pressed={emoji === e}
                      aria-label={`דמות ${e}`}
                      onClick={() => onProfileChange({ emoji: e })}
                    >
                      {e}
                    </button>
                  ))}
                </div>
                {hasExtraEmojis && (
                  <button
                    className="emoji-expand-btn"
                    onClick={() => setEmojiExpanded((v) => !v)}
                  >
                    {emojiExpanded ? "הצג פחות ▲" : "הצג עוד ▼"}
                  </button>
                )}
              </div>

              <div className="sidebar-field">
                <label className="sidebar-label">צבע</label>
                <div className="palette-grid">
                  {COLOR_PALETTES.map((p) => (
                    <button
                      key={p.id}
                      className={`palette-chip ${(colorPalette ?? "purple") === p.id ? "active" : ""}`}
                      style={{ background: p.primary }}
                      aria-pressed={(colorPalette ?? "purple") === p.id}
                      aria-label={`צבע ${p.id}`}
                      onClick={() => onProfileChange({ colorPalette: p.id })}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </fieldset>
        {saving && <p role="status">שומרים את הבחירה…</p>}
        {saveError && <div role="alert"><p>{saveError}</p>{onRetrySave && <button type="button" onClick={onRetrySave} disabled={saving}>ניסיון נוסף</button>}</div>}

        {/* Learning controls are scoped to the activity that actually uses them. */}
        {activity === "reading" ? <div className="sidebar-section">
          <span className="sidebar-section-title">הגדרות קריאה</span>
          <p className="sidebar-value">את שפת הטקסט ואורכו או רמת האנגלית בוחרים לפני יצירת הטקסט.</p>
        </div> : <div className="sidebar-section">
          <span className="sidebar-section-title">הגדרות אוצר מילים</span>

          <div className="sidebar-field">
            <label className="sidebar-label">קול המורה</label>
            <div className="teacher-grid">
              {PERSONA_PROFILES.filter((p) => p.id !== "default").map((p) => (
                <button
                  key={p.id}
                  className={`teacher-chip ${personaProfileId === p.id ? "active" : ""}`}
                  aria-pressed={personaProfileId === p.id}
                  onClick={() => onPersonaProfileChange?.(personaProfileId === p.id ? "default" : p.id)}
                  title={p.description}
                >
                  <span className="teacher-chip-emoji" aria-hidden="true">{p.emoji}</span>
                  <span className="teacher-chip-label">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-field">
            <label className="sidebar-label">שלב באוצר מילים</label>
            {readOnly ? (
              <span className="sidebar-value">
                {step} ({progression.find((s) => s.step === step)?.cefrLevel ?? ""})
              </span>
            ) : (
              <div className="step-grid" role="group" aria-label="בחירת שלב">
                {progression.map((s) => (
                  <button
                    key={s.step}
                    className={`step-chip ${s.step === step ? "active" : ""}`}
                    aria-pressed={s.step === step}
                    onClick={() => onStepChange?.(s.step)}
                  >
                    {s.step}
                    <span className="step-level">{s.cefrLevel}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>}
      </div>
    </>
  );
}
